import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getChildren as getChildrenApi,
  createChild as createChildApi,
  updateChild as updateChildApi,
  deleteChild as deleteChildApi,
  getCalendarEvents as getCalendarEventsApi,
  createCalendarEvent as createCalendarEventApi,
  updateCalendarEvent as updateCalendarEventApi,
  deleteCalendarEvent as deleteCalendarEventApi,
  createChildCalendar as createChildCalendarApi,
  uploadChildAvatar as uploadChildAvatarApi,
  removeChildAvatar as removeChildAvatarApi,
  getChildLikes as getChildLikesApi,
  unlikeActivity as unlikeActivityApi,
  getChildInterests as getChildInterestsApi,
  setChildInterests as setChildInterestsApi,
  getChildConnections as getChildConnectionsApi,
  addChildConnection as addChildConnectionApi,
  removeChildConnection as removeChildConnectionApi,
} from '../../util/api';

// ─── Async Thunks ───

const fetchChildrenThunk = createAsyncThunk(
  'DashboardPage/fetchChildren',
  async (_, { rejectWithValue }) => {
    try { return await getChildrenApi(); }
    catch (e: any) { return rejectWithValue(e.message); }
  }
);

const createChildThunk = createAsyncThunk(
  'DashboardPage/createChild',
  async (values: { childName: string; dateOfBirth: string }, { rejectWithValue }) => {
    try { return await createChildApi(values); }
    catch (e: any) { return rejectWithValue(e.message); }
  }
);

const updateChildThunk = createAsyncThunk(
  'DashboardPage/updateChild',
  async ({ childId, ...data }: { childId: string; [k: string]: any }, { rejectWithValue }) => {
    try { return await updateChildApi({ childId, ...data }); }
    catch (e: any) { return rejectWithValue(e.message); }
  }
);

const deleteChildThunk = createAsyncThunk(
  'DashboardPage/deleteChild',
  async (childId: string, { rejectWithValue }) => {
    try { await deleteChildApi({ childId, removeFromAll: false }); return childId; }
    catch (e: any) { return rejectWithValue(e.message); }
  }
);

const fetchEventsThunk = createAsyncThunk(
  'DashboardPage/fetchEvents',
  async (calendarId: string, { rejectWithValue }) => {
    try { return await getCalendarEventsApi(calendarId); }
    catch (e: any) { return rejectWithValue(e.message); }
  }
);

const fetchFamilyCalendarThunk = createAsyncThunk(
  'DashboardPage/fetchFamilyCalendar',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { children } = (getState() as any).DashboardPage;
      const withCals = (children || []).filter((c: any) => c.calendarId);
      if (!withCals.length) return {};
      const results = await Promise.all(
        withCals.map((c: any) =>
          getCalendarEventsApi(c.calendarId)
            .then((events: any) => ({ childId: c.id, events }))
            .catch(() => ({ childId: c.id, events: [] }))
        )
      );
      const map: Record<string, any[]> = {};
      results.forEach(({ childId, events }) => { map[childId] = events; });
      return map;
    } catch (e: any) { return rejectWithValue(e.message); }
  }
);

const fetchLikesThunk = createAsyncThunk(
  'DashboardPage/fetchLikes',
  async (childId: string) => {
    try { const d = await getChildLikesApi(childId); return d.likes || d || []; }
    catch { return []; }
  }
);

const fetchInterestsThunk = createAsyncThunk(
  'DashboardPage/fetchInterests',
  async (childId: string) => {
    try { const d = await getChildInterestsApi(childId); return d.interests || d || []; }
    catch { return []; }
  }
);

const fetchConnectionsThunk = createAsyncThunk(
  'DashboardPage/fetchConnections',
  async (childId: string) => {
    try { const d = await getChildConnectionsApi(childId); return d.connections || d || []; }
    catch { return []; }
  }
);

// ─── Slice ───

const slice = createSlice({
  name: 'DashboardPage',
  initialState: {
    children: [] as any[],
    fetchChildrenInProgress: false,
    fetchChildrenError: null as string | null,
    childMutationInProgress: false,
    childMutationError: null as string | null,
    selectedChildId: null as string | null,
    calendarEvents: [] as any[],
    fetchEventsInProgress: false,
    fetchEventsError: null as string | null,
    eventMutationInProgress: false,
    eventMutationError: null as string | null,
    familyCalendarEvents: {} as Record<string, any[]>,
    fetchFamilyCalendarInProgress: false,
    fetchFamilyCalendarError: null as string | null,
    childLikes: [] as any[],
    childInterests: [] as any[],
    childConnections: [] as any[],
    preferencesInProgress: false,
  },
  reducers: {
    selectChild: (s, a) => { s.selectedChildId = a.payload; s.calendarEvents = []; },
    setChildren: (s, a) => { s.children = a.payload; },
    patchChild: (s, a) => {
      const idx = s.children.findIndex((c: any) => c.id === a.payload.id);
      if (idx >= 0) s.children[idx] = { ...s.children[idx], ...a.payload };
    },
    preferencesStart: s => { s.preferencesInProgress = true; },
    preferencesDone: s => { s.preferencesInProgress = false; },
  },
  extraReducers: builder => {
    builder
      // Children fetch
      .addCase(fetchChildrenThunk.pending, s => { s.fetchChildrenInProgress = true; s.fetchChildrenError = null; })
      .addCase(fetchChildrenThunk.fulfilled, (s, a) => { s.fetchChildrenInProgress = false; s.children = a.payload; })
      .addCase(fetchChildrenThunk.rejected, (s, a) => { s.fetchChildrenInProgress = false; s.fetchChildrenError = a.payload as string; })
      // Create child
      .addCase(createChildThunk.pending, s => { s.childMutationInProgress = true; s.childMutationError = null; })
      .addCase(createChildThunk.fulfilled, (s, a) => { s.childMutationInProgress = false; s.children.push(a.payload); })
      .addCase(createChildThunk.rejected, (s, a) => { s.childMutationInProgress = false; s.childMutationError = a.payload as string; })
      // Update child
      .addCase(updateChildThunk.pending, s => { s.childMutationInProgress = true; s.childMutationError = null; })
      .addCase(updateChildThunk.fulfilled, (s, a) => {
        s.childMutationInProgress = false;
        const idx = s.children.findIndex((c: any) => c.id === a.payload.id);
        if (idx >= 0) s.children[idx] = { ...s.children[idx], ...a.payload };
      })
      .addCase(updateChildThunk.rejected, (s, a) => { s.childMutationInProgress = false; s.childMutationError = a.payload as string; })
      // Delete child
      .addCase(deleteChildThunk.pending, s => { s.childMutationInProgress = true; s.childMutationError = null; })
      .addCase(deleteChildThunk.fulfilled, (s, a) => {
        s.childMutationInProgress = false;
        s.children = s.children.filter((c: any) => c.id !== a.payload);
        if (s.selectedChildId === a.payload) s.selectedChildId = null;
      })
      .addCase(deleteChildThunk.rejected, (s, a) => { s.childMutationInProgress = false; s.childMutationError = a.payload as string; })
      // Calendar events
      .addCase(fetchEventsThunk.pending, s => { s.fetchEventsInProgress = true; s.fetchEventsError = null; })
      .addCase(fetchEventsThunk.fulfilled, (s, a) => { s.fetchEventsInProgress = false; s.calendarEvents = a.payload; })
      .addCase(fetchEventsThunk.rejected, (s, a) => { s.fetchEventsInProgress = false; s.fetchEventsError = a.payload as string; })
      // Family calendar
      .addCase(fetchFamilyCalendarThunk.pending, s => { s.fetchFamilyCalendarInProgress = true; s.fetchFamilyCalendarError = null; })
      .addCase(fetchFamilyCalendarThunk.fulfilled, (s, a) => { s.fetchFamilyCalendarInProgress = false; s.familyCalendarEvents = a.payload; })
      .addCase(fetchFamilyCalendarThunk.rejected, (s, a) => { s.fetchFamilyCalendarInProgress = false; s.fetchFamilyCalendarError = a.payload as string; })
      // Preferences
      .addCase(fetchLikesThunk.fulfilled, (s, a) => { s.childLikes = a.payload; })
      .addCase(fetchInterestsThunk.fulfilled, (s, a) => { s.childInterests = a.payload; })
      .addCase(fetchConnectionsThunk.fulfilled, (s, a) => { s.childConnections = a.payload; });
  },
});

const { setChildren, patchChild, preferencesStart, preferencesDone } = slice.actions;

export default slice.reducer;

// ─── Exported Thunks ───

export const fetchChildren = () => (dispatch: any) => dispatch(fetchChildrenThunk()).unwrap();
export const createChild = (values: { childName: string; dateOfBirth: string }) => (dispatch: any) => dispatch(createChildThunk(values)).unwrap();
export const updateChild = (childId: string, data: Record<string, any>) => (dispatch: any) => dispatch(updateChildThunk({ childId, ...data })).unwrap();
export const removeChild = (childId: string) => (dispatch: any) => dispatch(deleteChildThunk(childId)).unwrap();
export const selectChild = (childId: string | null) => slice.actions.selectChild(childId);

export const uploadChildAvatar = (childId: string, file: Blob) => async (dispatch: any) => {
  const result = await uploadChildAvatarApi(childId, file);
  dispatch(patchChild({ id: childId, profileImageUrl: result.profileImageUrl }));
  return result;
};

export const removeChildAvatar = (childId: string) => async (dispatch: any) => {
  await removeChildAvatarApi(childId);
  dispatch(patchChild({ id: childId, profileImageUrl: null }));
};

// Calendar
export const fetchChildCalendarEvents = (calendarId: string) => (dispatch: any) => dispatch(fetchEventsThunk(calendarId)).unwrap();
export const fetchFamilyCalendar = () => (dispatch: any) => dispatch(fetchFamilyCalendarThunk());

export const createChildCalendarEvent = (calendarId: string, eventData: any) => async (dispatch: any) => {
  await createCalendarEventApi({ calendarId, ...eventData });
  dispatch(fetchEventsThunk(calendarId));
};

export const updateChildCalendarEvent = (calendarId: string, eventData: any) => async (dispatch: any) => {
  await updateCalendarEventApi({ calendarId, ...eventData });
  dispatch(fetchEventsThunk(calendarId));
};

export const deleteChildCalendarEvent = (calendarId: string, eventUrl: string) => async (dispatch: any) => {
  await deleteCalendarEventApi({ calendarId, eventUrl });
  dispatch(fetchEventsThunk(calendarId));
};

export const createChildCalendar = (childId: string) => async (dispatch: any, getState: any) => {
  const result = await createChildCalendarApi({ childId });
  const { children } = getState().DashboardPage;
  dispatch(setChildren(children.map((c: any) =>
    c.id === childId ? { ...c, calendarUrl: result.calendarUrl, calendarId: result.calendarId } : c
  )));
  return result;
};

// Preferences
export const fetchChildLikes = (childId: string) => (dispatch: any) => dispatch(fetchLikesThunk(childId));
export const fetchChildInterests = (childId: string) => (dispatch: any) => dispatch(fetchInterestsThunk(childId));
export const fetchChildConnections = (childId: string) => (dispatch: any) => dispatch(fetchConnectionsThunk(childId));

export const removeChildLike = (childId: string, activityId: string) => async (dispatch: any) => {
  dispatch(preferencesStart());
  try { await unlikeActivityApi({ childId, activityId }); dispatch(fetchLikesThunk(childId)); }
  finally { dispatch(preferencesDone()); }
};

export const updateChildInterests = (childId: string, categories: string[]) => async (dispatch: any) => {
  dispatch(preferencesStart());
  try { await setChildInterestsApi({ childId, categories }); dispatch(fetchInterestsThunk(childId)); }
  finally { dispatch(preferencesDone()); }
};

export const addConnection = (childId: string, connectedChildId: string, relationshipType: string) => async (dispatch: any) => {
  dispatch(preferencesStart());
  try { await addChildConnectionApi({ childId, connectedChildId, relationshipType }); dispatch(fetchConnectionsThunk(childId)); }
  finally { dispatch(preferencesDone()); }
};

export const removeConnection = (childId: string, connectionId: string) => async (dispatch: any) => {
  dispatch(preferencesStart());
  try { await removeChildConnectionApi({ childId, connectionId }); dispatch(fetchConnectionsThunk(childId)); }
  finally { dispatch(preferencesDone()); }
};

// ─── Selectors ───

export const selectChildLikes = (s: any) => s.DashboardPage?.childLikes || [];
export const selectChildInterests = (s: any) => s.DashboardPage?.childInterests || [];
export const selectChildConnections = (s: any) => s.DashboardPage?.childConnections || [];
export const selectPreferencesInProgress = (s: any) => s.DashboardPage?.preferencesInProgress || false;

// ─── loadData ───

export const loadData = () => (dispatch: any) => dispatch(fetchChildrenThunk());
